import { type Dispatch, type JSX, memo, type SetStateAction, useCallback, useRef } from 'react';

import { useTranslation } from '@cx-modules/i18n-provider';
import { Arrow } from '@cx-modules/ui';
import { mergePersistentParams } from '@cx-modules/utils';

import { useMatchesCouponContext } from '../../hooks';
import { trackEvent } from '../../utils';

import { useAlignLabelsWithBets } from './hooks';
import { ArrowWrapper, LinkText, SeeAllOdds, SeeAllOddsContainer } from './style';

interface AllOddsLinkProps {
  requestUrl: string;
  setGroupTitleLabelsMarginRight: Dispatch<SetStateAction<number>>;
  subeventId: number;
  url: string;
}

const AllOddsLink = ({
  requestUrl,
  setGroupTitleLabelsMarginRight,
  subeventId,
  url,
}: AllOddsLinkProps): JSX.Element => {
  const seeAllOddsContainerRef = useRef<HTMLDivElement>(null);

  useAlignLabelsWithBets({
    seeAllOddsContainerRef,
    setGroupTitleLabelsMarginRight,
  });

  const analyticsDataLayer = useMatchesCouponContext(state => state.analyticsDataLayer);
  const analyticsImpressionEventData = useMatchesCouponContext(state => state.analyticsImpressionEventData);
  const eventId = useMatchesCouponContext(state => state.eventId);
  const eventName = useMatchesCouponContext(state => state.eventName);

  const handleClick = useCallback(() => {
    trackEvent(
      'matches_coupon_all_odds_click',
      { eventId, eventName, subeventId, ...analyticsImpressionEventData },
      analyticsDataLayer,
    );
  }, [analyticsDataLayer, analyticsImpressionEventData, eventId, eventName, subeventId]);

  const { t } = useTranslation('label');
  const processedUrl = url.replace('/in-running', '');
  const linkAttr: JSX.IntrinsicElements['a'] = {
    href: mergePersistentParams(processedUrl, requestUrl),
    onClick: handleClick,
    rel: 'noopener',
  };

  return (
    <SeeAllOddsContainer ref={seeAllOddsContainerRef}>
      <SeeAllOdds {...linkAttr} data-testid="see-all-odds-link" data-track="see-all-odds-link">
        <LinkText>{t('CX_MORE_ODDS', { ns: 'label' })}</LinkText>
        <ArrowWrapper>
          <Arrow borderWidth={1} colour="var(--ogm-timberwolves)" right size={3} x={0} y={-2} />
        </ArrowWrapper>
      </SeeAllOdds>
    </SeeAllOddsContainer>
  );
};

export default memo(AllOddsLink);
